/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql, useQuery } from '@apollo/client';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconPlus from '@apollo/icons/default/IconPlus.svg';
import { Button, Link, Radio, Select, Switch } from '@apollo/orbit';
import { jsx } from '@emotion/react';
import classnames from 'classnames';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect } from 'react';
import {
  Link as ReactRouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useThemeContext } from '../../../../components/themeProvider/ThemeProvider';
import { GraphRef } from '../../hooks/useGraphRef';
import { useGraphVisibility } from '../../hooks/useGraphVisibility';
import { useIsLoggedIn } from '../../hooks/useIsLoggedIn';
import { sandboxGraphRouteConfig } from '../../routes';
import { SandboxLogInButton } from '../../sandboxShared/SandboxSignUpButtons';
import { StickyHeader } from '../components/stickyHeader/StickyHeader';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import type { PublicUrlConfirmation } from '../hooks/useExplorerState/useHasPublicUrlBeenConfirmed';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../hooks/usePerGraphIdentifierLocalStorage';
import { TracesInstructions } from '../tracesInstructions/TracesInstructions';

import { ExplorerSettingsType } from './useExplorerSettings';

const ResponseHints = GraphQLTypes.ResponseHints;

const Setting = ({
  analyticsId,
  isChecked,
  onChange,
  children,
  label,
  isDisabled,
  disabledTooltipContent,
  noBorder,
}: {
  analyticsId: string;
  label: React.ReactNode;
  isChecked: React.ComponentProps<typeof Switch>['isChecked'];
  onChange: React.ComponentProps<typeof Switch>['onChange'];
  children?: React.ReactNode;
  isDisabled?: boolean;
  disabledTooltipContent?: React.ReactNode;
  noBorder?: boolean;
}) => {
  const describedBySetting = useAriaDescribedBySetting(
    'explorer-setting-description-',
  );
  const tooltipDisabled = !disabledTooltipContent || !isDisabled;

  return (
    <div
      className={classnames(
        'my-4',
        !noBorder && 'border-t border-primary pt-4',
      )}
    >
      <Tooltip
        label={disabledTooltipContent}
        placement="top-end"
        disabled={tooltipDisabled}
        interactive={!tooltipDisabled}
      >
        <div className="flex items-center">
          <span className="flex-1 font-semibold">{label}</span>
          <Switch
            onChange={onChange}
            isChecked={isChecked}
            isDisabled={isDisabled}
            data-analytics-label={`explorer_setting ${analyticsId} ${
              isChecked ? 'on' : 'off'
            }`}
            data-analytics-category="Explorer"
            aria-describedby={describedBySetting}
          >
            {isChecked ? 'on' : 'off'}
          </Switch>
        </div>
      </Tooltip>
      {children && (
        <div
          className={classnames(
            'mt-2 text-sm',
            isDisabled ? 'text-placeholder' : 'text-secondary',
          )}
          aria-hidden
          id={describedBySetting}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const RadioListElement = ({
  disabledMessage,
  checked,
  title,
  onChange,
  children,
}: {
  disabledMessage?: string;
  checked: boolean;
  title: string;
  onChange: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Tooltip label={disabledMessage} disabled={!disabledMessage}>
      <label className="mt-3 flex cursor-pointer items-start">
        <Radio
          isDisabled={!!disabledMessage}
          isChecked={checked}
          className="mr-2 mt-1"
          onChange={onChange}
        />
        <div className={classnames(!!disabledMessage && 'text-disabled')}>
          <div className="font-semibold">{title}</div>
          <div>{children}</div>
        </div>
      </label>
    </Tooltip>
  );
};

const percentileLabelMap = {
  0.99: '99th',
  0.95: '95th',
  0.9: '90th',
  0.5: '50th',
};

const ScriptEditorSection = ({
  scriptValue,
  graphRef,
  scriptEditor,
  isEnabled,
  isSandboxRoute,
}: {
  scriptValue?: string;
  graphRef: GraphRef | null;
  scriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  isEnabled?: boolean;
  isSandboxRoute?: boolean;
}) => {
  const location = useLocation<{
    referrer: 'tab' | 'settings';
  }>();
  return (
    <>
      <div className="mb-2 mt-4 flex flex-row items-center justify-between text-primary">
        <b className={classnames(!scriptValue && 'text-secondary')}>
          Preflight Script
        </b>
        <PermissionGuard
          graphId={graphRef?.graphId}
          graphVariantPermissions="canManageExplorerSettings"
          graphVariant={graphRef?.graphVariant}
          fallback={null}
        >
          <Button
            as={ReactRouterLink}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.preflightScript,
            })}
            size="sm"
            variant="hidden"
            leftIcon={scriptValue ? <IconEdit /> : <IconPlus />}
          >
            {scriptValue ? 'Edit' : 'Add script'}
          </Button>
        </PermissionGuard>
      </div>
      <div className="mb-2 space-y-6 text-sm text-secondary">
        <p className={classnames(!scriptValue && 'text-secondary')}>
          This script is run before each operation submitted and is often used
          for automated authentication.
          {!isSandboxRoute && (
            <> It can only be added or modified by admins.</>
          )}{' '}
          <Link
            isExternal
            to={DocsRouter.path('ExplorerConnectingWithHash', {
              hash: 'preflight-scripts',
            })}
          >
            Learn more
          </Link>
        </p>
        {scriptValue && (
          <p>
            {isEnabled ? (
              <>
                Your preflight script is currently{' '}
                <span className="font-semibold">running</span>. To turn it off
              </>
            ) : (
              <>
                Your preflight script is currently{' '}
                <span className="font-semibold">not running</span>. To turn it
                on
              </>
            )}
            , scroll down to your Explorer{' '}
            <Link to="#scripts">Personal Settings</Link>.
          </p>
        )}
      </div>
      {scriptValue && (
        <div
          // Exclude scripts from fullstory
          className="fs-exclude mt-1 h-32 rounded bg-primary text-primary"
        >
          <StudioEditorContainer
            editor={scriptEditor}
            className={classnames('h-full', !isEnabled && 'opacity-50')}
          />
        </div>
      )}
    </>
  );
};

export const ExplorerSettings = React.memo(
  ({
    graphRef,
    settings: {
      responseHints,
      setResponseHints,
      explorerManageVariablesSetting,
      setExplorerManageVariablesSetting,
      isMockingResponses,
      setIsMockingResponses,
      setThemeName,
      fieldTimingsPercentile,
      setFieldTimingsPercentile,
      showTraces,
      requestQueryPlan,
      setShowTraces,
      setShowQueryPlan,
      explorerLinkSettings,
      preflightScript,
      isPreflightScriptEnabled,
      setPreflightScriptsEnabled,
      isOperationScriptEnabled,
      setOperationScriptsEnabled,
      loading: settingsLoading,
      requestConnectorsDebugging,
      setRequestConnectorsDebugging,
    },
    settingsEndpointUrl,
    settingsSubscriptionUrl,
    openEditUrlModal,
    explorerBehavioralFlags,
    hasPublicUrlBeenConfirmed,
    canBeEmbedded,
    environmentVariablesEditor,
  }: {
    graphRef: GraphRef | null;
    settings: ExplorerSettingsType;
    settingsEndpointUrl: string | null;
    settingsSubscriptionUrl: string | null;
    openEditUrlModal: () => void;
    explorerBehavioralFlags: ExplorerBehavioralFlags;
    hasPublicUrlBeenConfirmed: PublicUrlConfirmation;
    canBeEmbedded: boolean;
    environmentVariablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  }) => {
    const { themedValue } = useThemeContext();
    const isDarkMode = themedValue({ dark: true, light: false });
    const location = useLocation<{
      referrer: 'tab' | 'settings';
    }>();
    const graphVisibilityType = useGraphVisibility();
    const isPublicRoute = graphVisibilityType === 'public';
    const showConnectorsSetting = useLDFlag('connectors-show-debug-pane');

    const { data, loading: dataLoading } = useQuery<
      GraphQLTypes.ExplorerSettingsQuery,
      GraphQLTypes.ExplorerSettingsQueryVariables
    >(
      gql`
        query ExplorerSettingsQuery($graphId: ID!, $graphVariant: String!) {
          service(id: $graphId) {
            id
            roles {
              canQueryStats
            }
            account {
              id
            }
            graphType
            schemaTag(tag: $graphVariant) {
              id
              variant {
                id
                url
                preflightScript
                sharedHeaders
                isPublic
                hasSupergraphSchema
              }
            }
          }
        }
      `,
      {
        variables: {
          graphId: graphRef?.graphId as string,
          graphVariant: graphRef?.graphVariant as string,
        },
        skip: !graphRef,
      },
    );

    const url = data?.service?.schemaTag?.variant?.url;

    const hasSupergraphSchema =
      !!data?.service?.schemaTag?.variant?.hasSupergraphSchema;
    const isSandboxRoute =
      useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

    const embedLabel = isSandboxRoute ? 'Sandbox' : 'Explorer';

    // Fix invalid states for hints.
    React.useEffect(() => {
      if (dataLoading) {
        return;
      }
      if (
        (responseHints === ResponseHints.TRACE_TIMINGS && !showTraces) ||
        (responseHints === ResponseHints.SUBGRAPHS && !hasSupergraphSchema)
      ) {
        setResponseHints(ResponseHints.NONE);
      }
    }, [
      responseHints,
      setResponseHints,
      showTraces,
      hasSupergraphSchema,
      dataLoading,
    ]);

    const preflightScriptsEditor = useStudioEditor({
      ...MONACO_READONLY_OPTIONS,
      language: 'javascript',
      defaultValue: preflightScript,
    });

    // If we have a response hints on, but have not confirmed the url, we should
    // switch the toggle to off automatically
    React.useEffect(() => {
      if (responseHints === ResponseHints.SAMPLE_RESPONSES) {
        hasPublicUrlBeenConfirmed({
          url,
          preflightScript,
          shouldPrompt: true,
          canApproveSingleRun: false,
          isSubscription: false,
        }).then((hasBeenConfirmed) => {
          if (!hasBeenConfirmed) {
            setResponseHints(ResponseHints.NONE);
          }
        });
      }
    }, [
      responseHints,
      hasPublicUrlBeenConfirmed,
      setResponseHints,
      url,
      preflightScript,
    ]);
    const [hasTouchedDarkModeToggle, setHasTouchedDarkModeToggle] =
      useLocalStorage('hasTouchedDarkModeToggle');
    useEffect(() => {
      if (!preflightScript && !settingsLoading) {
        setPreflightScriptsEnabled?.(false);
      }
    }, [preflightScript, setPreflightScriptsEnabled, settingsLoading]);
    const { isLoggedIn } = useIsLoggedIn();
    const canSeePreflightScripts =
      ((!isPublicRoute || preflightScript) &&
        graphRef &&
        explorerBehavioralFlags.canSeePreflightScripts) ||
      (isLoggedIn && isSandboxRoute);

    const localStorageGraphIdentifierForSubscriptionProtocol =
      useLocalStorageGraphIdentifier({
        shouldNamespaceSandboxByEndpoint: true,
        shouldOnlyStoreForRegisteredGraphs: false,
      });
    const [protocol] = usePerGraphIdentifierLocalStorage({
      key: 'graphqlSubscriptionProtocol',
      graphIdentifier: localStorageGraphIdentifierForSubscriptionProtocol,
      stableInitialValue: 'auto-detect',
    });

    const isCloudSupergraph =
      data?.service?.graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH;

    return (
      <>
        {
          // if this graph can't see preflight scripts or connection settings, there is nothing to show in this section
          !isSandboxRoute &&
            (canSeePreflightScripts ||
              explorerBehavioralFlags.showConnectionSettings) && (
              <StickyHeader className="flex items-center">
                Shared Settings
                <Tooltip label="These settings are shared across your organization and will apply to all users of your graph">
                  <IconInfo className="ml-2" />
                </Tooltip>
              </StickyHeader>
            )
        }
        {explorerBehavioralFlags.showConnectionSettings && (
          <>
            <div className="mt-4 flex flex-row items-center justify-between">
              <b>Connection settings</b>
              <PermissionGuard
                graphId={graphRef?.graphId}
                graphVariantPermissions="canManageExplorerSettings"
                graphVariant={graphRef?.graphVariant}
                fallback={null}
              >
                <Button
                  onClick={openEditUrlModal}
                  size="sm"
                  variant="hidden"
                  leftIcon={<IconEdit />}
                >
                  Edit
                </Button>
              </PermissionGuard>
            </div>
            <div className="mb-2 text-sm text-secondary">
              <p>
                <b>Endpoint:</b> {settingsEndpointUrl}
              </p>
              <div>
                {!isCloudSupergraph &&
                  settingsSubscriptionUrl &&
                  protocol !== 'http-multipart' && (
                    <p>
                      <b>Subscriptions endpoint:</b> {settingsSubscriptionUrl}
                    </p>
                  )}
              </div>
            </div>
          </>
        )}

        {canSeePreflightScripts && (
          <ScriptEditorSection
            scriptValue={preflightScript}
            graphRef={graphRef}
            scriptEditor={preflightScriptsEditor}
            isEnabled={isPreflightScriptEnabled}
            isSandboxRoute={isSandboxRoute}
          />
        )}

        {!canSeePreflightScripts && isSandboxRoute && !isLoggedIn && (
          <div className="my-4 mb-1 flex flex-col justify-between border-t border-primary py-4 text-primary">
            <b>Preflight script</b>
            <div className="mt-2 flex flex-col items-center text-center text-primary">
              <div className="flex flex-col items-center">
                <div className="text-sm text-secondary">
                  Log in to your Studio account to use preflight scripts.
                </div>
                <SandboxLogInButton
                  userSegmentType="sandbox_preflight_scripts"
                  className="mt-4"
                />
              </div>
            </div>
          </div>
        )}

        {!isSandboxRoute && (
          <StickyHeader className="flex items-center">
            Personal Settings
            <Tooltip label="These settings are just for you">
              <IconInfo className="ml-2" />
            </Tooltip>
          </StickyHeader>
        )}

        {explorerBehavioralFlags.showThemeToggle && (
          <Setting
            analyticsId="dark mode"
            isChecked={isDarkMode}
            onChange={() => {
              setThemeName(
                themedValue({
                  dark: GraphQLTypes.ThemeName.LIGHT,
                  light: GraphQLTypes.ThemeName.DARK,
                }),
              );
              if (!hasTouchedDarkModeToggle) setHasTouchedDarkModeToggle(true);
            }}
            label="Dark mode"
            noBorder
          />
        )}

        <Setting
          analyticsId="auto create variables"
          isChecked={explorerManageVariablesSetting}
          onChange={() => {
            setExplorerManageVariablesSetting(!explorerManageVariablesSetting);
          }}
          label="Auto create variables"
          noBorder={!explorerBehavioralFlags.showThemeToggle}
        >
          When autocompleting variables in the editor, the Explorer will
          generate extracted variable syntax for you.
        </Setting>

        {/* For editor hints, only show the response hints on sandbox or public routes */}
        {isPublicRoute || !graphRef ? (
          <Setting
            analyticsId="response hints"
            isChecked={responseHints !== GraphQLTypes.ResponseHints.NONE}
            onChange={() => {
              setResponseHints(
                responseHints === GraphQLTypes.ResponseHints.NONE
                  ? GraphQLTypes.ResponseHints.SAMPLE_RESPONSES
                  : GraphQLTypes.ResponseHints.NONE,
              );
            }}
            label="Response hints"
          >
            Run partial queries for new fields as you add them and show the
            results inline in the editor.
          </Setting>
        ) : (
          <Setting
            analyticsId="editor hints"
            isChecked={responseHints !== GraphQLTypes.ResponseHints.NONE}
            onChange={() => {
              setResponseHints(
                responseHints === GraphQLTypes.ResponseHints.NONE
                  ? GraphQLTypes.ResponseHints.SAMPLE_RESPONSES
                  : GraphQLTypes.ResponseHints.NONE,
              );
            }}
            label="Editor hints"
          >
            Show metadata about each of your operation's fields, right-aligned
            in the Editor.
            {responseHints !== GraphQLTypes.ResponseHints.NONE && (
              <>
                <RadioListElement
                  onChange={() => {
                    setResponseHints(
                      GraphQLTypes.ResponseHints.SAMPLE_RESPONSES,
                    );
                  }}
                  checked={
                    responseHints ===
                    GraphQLTypes.ResponseHints.SAMPLE_RESPONSES
                  }
                  title="Response hints"
                >
                  Run partial queries for new fields as you add them and show
                  the results inline in the editor.
                </RadioListElement>
                {hasSupergraphSchema && (
                  <RadioListElement
                    onChange={() => {
                      setResponseHints(GraphQLTypes.ResponseHints.SUBGRAPHS);
                    }}
                    checked={
                      responseHints === GraphQLTypes.ResponseHints.SUBGRAPHS
                    }
                    title="Subgraph source"
                  >
                    Show which subgraph(s) that each field in your operation is
                    sourced from.
                  </RadioListElement>
                )}
                {graphRef && (
                  <RadioListElement
                    disabledMessage={
                      !data?.service?.roles?.canQueryStats
                        ? `You can't turn this on because you do not have access to view performance metrics for this graph.`
                        : undefined
                    }
                    checked={
                      responseHints === GraphQLTypes.ResponseHints.TIMINGS
                    }
                    onChange={() => {
                      setResponseHints(GraphQLTypes.ResponseHints.TIMINGS);
                    }}
                    title="Field latency approximations"
                  >
                    Show the following percentile latencies for fields in your
                    operation:{' '}
                    {responseHints === GraphQLTypes.ResponseHints.TIMINGS ? (
                      <Select
                        className=""
                        size="xs"
                        value={fieldTimingsPercentile}
                        onChange={(e) => {
                          setFieldTimingsPercentile(
                            e.target.value as typeof fieldTimingsPercentile,
                          );
                        }}
                      >
                        {Object.entries(percentileLabelMap).map(
                          ([value, label]) => (
                            <option value={value} key={label}>
                              {label}
                            </option>
                          ),
                        )}
                      </Select>
                    ) : (
                      // </>
                      percentileLabelMap[fieldTimingsPercentile]
                    )}
                  </RadioListElement>
                )}
              </>
            )}
          </Setting>
        )}

        <Setting
          analyticsId="mock responses"
          isChecked={isMockingResponses}
          onChange={() => {
            setIsMockingResponses(!isMockingResponses);
          }}
          label="Mock responses"
        >
          Instead of sending requests to your endpoint, mock out the responses
          locally within the Explorer.
        </Setting>

        {explorerBehavioralFlags.shouldShowCopyLink && (
          <div className="my-4 border-t border-primary pt-4">
            <div className="mb-1 flex flex-row items-center justify-between">
              <b>Shareable operation link</b>
              <Button
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.explorerShareableLinkSettings,
                })}
                size="sm"
                variant="hidden"
                leftIcon={<IconEdit />}
              >
                Edit
              </Button>
            </div>
            <span className="text-sm text-secondary">
              Settings for generated links to explorer documents. Currently set
              to {!explorerLinkSettings.includeVariables && ' not'} include
              variables and {!explorerLinkSettings.includeHeaders && ' not'}{' '}
              include headers.
            </span>
          </div>
        )}
        {canBeEmbedded && (
          <div className="my-4 mb-1 flex flex-row items-center justify-between border-t border-primary py-4">
            <b>Embed {embedLabel}</b>
            <Button
              data-analytics-label={`Embed ${embedLabel} CTA: Explorer Settings`}
              data-analytics-category={`Embeddable ${embedLabel}`}
              as={ReactRouterLink}
              to={{
                ...setOverlayVisibleLocation({
                  location,
                  overlayName: isSandboxRoute
                    ? Config.modals.embedSandbox
                    : Config.modals.embedExplorer,
                }),
                state: {
                  embedModalTabState: undefined,
                },
              }}
              size="sm"
              variant="hidden"
              leftIcon={<IconCode />}
            >
              Copy code snippet
            </Button>
          </div>
        )}
        <div className="relative">
          {/* NOTE: This helps us place the scripts section at the top of the
                      personal setting section when users are linked to it
                      without having it be overlapped by the sticky header. */}
          <div id="scripts" className="absolute -top-12" />
          <div className="mb-4 border-t border-primary pt-4">
            <div className="mb-1 flex flex-row items-center justify-between">
              <b>Scripts</b>
            </div>
            <Setting
              analyticsId="preflightScripts"
              isChecked={isPreflightScriptEnabled}
              onChange={() => {
                setPreflightScriptsEnabled?.((v) => !v);
              }}
              noBorder
              label={<b className="text-sm">Preflight script</b>}
              isDisabled={!preflightScript}
              disabledTooltipContent={
                <PermissionGuard
                  graphId={graphRef?.graphId}
                  graphVariantPermissions="canManageExplorerSettings"
                  graphVariant={graphRef?.graphVariant}
                  fallback={
                    <>
                      Ask your admin to add a preflight script for this variant.{' '}
                      <Link
                        className="whitespace-nowrap"
                        isExternal
                        to="https://www.apollographql.com/blog/announcement/tooling/automatically-authenticate-with-preflight-scripts-in-apollo-studio-explorer/"
                      >
                        Learn more about preflight scripts.
                      </Link>
                    </>
                  }
                >
                  You will need to add a script before turning this on
                </PermissionGuard>
              }
            >
              This script is run before each operation submitted. Often used for
              automated authentication.
            </Setting>
            <Setting
              analyticsId="operationScripts"
              isChecked={isOperationScriptEnabled}
              onChange={() => {
                setOperationScriptsEnabled?.((v) => !v);
              }}
              noBorder
              label={<b className="text-sm">Operation script</b>}
            >
              These scripts run after preflight scripts and before each
              operation they're assigned to. Often used for testing and
              debugging.
            </Setting>
          </div>
        </div>
        {explorerBehavioralFlags.environmentVariablesInSettings &&
          explorerBehavioralFlags.showHeadersAndEnvironmentVariables && (
            <div className="mb-4 border-t border-primary pt-4">
              <div className="mb-1 flex flex-row items-center justify-between">
                <b>Environment variables</b>
              </div>
              <span className="text-sm text-secondary">
                Define variables to use in your Headers and Shared Headers.
              </span>

              <div
                // Exclude the environment variables from fullstory
                className="fs-exclude relative mt-2 h-32 rounded bg-primary text-primary"
              >
                <div className="absolute right-0 top-0 z-20 mr-2 mt-2 px-2 py-1 text-xs uppercase tracking-wider text-secondary">
                  JSON
                </div>
                <StudioEditorContainer
                  editor={environmentVariablesEditor}
                  className="h-full"
                />
              </div>
            </div>
          )}

        {!isPublicRoute && (
          <Setting
            analyticsId="traces"
            isChecked={showTraces}
            onChange={() => {
              setShowTraces((v) => !v);
            }}
            label="Show inline traces"
          >
            <TracesInstructions />
          </Setting>
        )}
        {isSandboxRoute && (
          <Setting
            analyticsId={
              explorerBehavioralFlags.initialRequestQueryPlan
                ? 'router-query-plan-default-on'
                : 'router-query-plan-default-off'
            }
            isChecked={requestQueryPlan}
            onChange={() => {
              setShowQueryPlan((v) => !v);
            }}
            label="Request query plans from router"
          >
            The target endpoint needs to be an instance of Apollo Router that's
            been run with{' '}
            <Link to={DocsRouter.path('RoverDev')} isExternal>
              rover dev
            </Link>{' '}
            to display query plans.
          </Setting>
        )}
        {isSandboxRoute && showConnectorsSetting && (
          <Setting
            analyticsId={
              explorerBehavioralFlags.initialRequestConnectorsDebugging
                ? 'router-connectors-debugging-default-on'
                : 'router-connectors-debugging-default-off'
            }
            isChecked={requestConnectorsDebugging}
            onChange={() => {
              setRequestConnectorsDebugging((v) => !v);
            }}
            label="Request connectors debugging from router"
          >
            The target endpoint needs to be an instance of Apollo Router that's
            been run with{' '}
            <Link to={DocsRouter.path('RoverDev')} isExternal>
              rover dev
            </Link>{' '}
            to display connectors debugging information.
          </Setting>
        )}
      </>
    );
  },
);
